/**
 * Marketing emails of Gula de Agua: content blocks → HTML + plain text.
 *
 * Pure and dependency-free so the same code renders the live preview in the
 * CRM (browser) and the email that is sent (Deno edge function).
 */

export type EmailStyle = "brand" | "plain" | "html";

export type EmailBlock =
  | { id?: string; type: "heading"; text: string }
  | { id?: string; type: "text"; text: string }
  | { id?: string; type: "image"; url: string; alt?: string; link?: string }
  | {
      id?: string;
      type: "button";
      label: string;
      url: string;
      variant?: "dark" | "lime";
    }
  | {
      id?: string;
      type: "video";
      url: string;
      thumbnail?: string;
      label?: string;
    }
  | { id?: string; type: "file"; url: string; name?: string; label?: string }
  | { id?: string; type: "html"; html: string }
  | { id?: string; type: "divider" };

export type EmailBlockType = EmailBlock["type"];

export interface EmailContent {
  subject: string;
  preheader?: string;
  style: EmailStyle;
  blocks: EmailBlock[];
}

export interface EmailLink {
  url: string;
  label: string;
}

export interface RenderOptions {
  /** First name of the recipient; replaces {nombre} */
  firstName?: string | null;
  /** Tracked URL for the n-th link of the email (n = position in `links`) */
  trackLink?: (index: number, url: string) => string;
  /** 1×1 image that records the open */
  openPixelUrl?: string;
  /** Page to unsubscribe (always shown in the footer) */
  unsubscribeUrl?: string;
}

export interface RenderedEmail {
  subject: string;
  html: string;
  text: string;
  /** Links of the email in order (the index used by trackLink) */
  links: EmailLink[];
}

export const GULA_PHONE = "614 404 301";
export const GULA_WHATSAPP = "https://wa.me/34614404301";
export const GULA_WEB = "https://guladeagua.es";
const INK = "#111111";
const LIME = "#a3e635";
const LIME_SOFT = "#ecfccb";
const MUTED = "#6b6b6b";

export const escapeHtml = (value: unknown): string =>
  String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

/** "salvador riestra" → "Salvador" */
export const greetingName = (firstName?: string | null): string => {
  const first = (firstName ?? "").trim().split(/\s+/)[0] ?? "";
  return first
    ? first.charAt(0).toLocaleUpperCase("es") +
        first.slice(1).toLocaleLowerCase("es")
    : "";
};

/**
 * {nombre} → the first name. Without a name it disappears with the space in
 * front, so "Hola {nombre}," reads "Hola,".
 */
export const fillName = (value: string, firstName?: string | null): string => {
  const name = greetingName(firstName);
  return value.replace(/\s?\{\s*nombre\s*\}/gi, (match) =>
    name ? match.replace(/\{\s*nombre\s*\}/i, name) : "",
  );
};

/** YouTube link → its thumbnail, so a video shows as a picture with play. */
export const youtubeThumbnail = (url: string): string | null => {
  const match = url.match(
    /(?:youtube\.com\/(?:watch\?(?:.*&)?v=|shorts\/|embed\/)|youtu\.be\/)([\w-]{11})/,
  );
  return match ? `https://img.youtube.com/vi/${match[1]}/hqdefault.jpg` : null;
};

const isTrackable = (url: string) => /^https?:\/\//i.test(url.trim());

/** Text of a link, or the alt of its image. */
const linkLabel = (inner: string) =>
  stripTags(inner) || inner.match(/\balt=(["'])(.*?)\1/i)?.[2]?.trim() || "";

const stripTags = (html: string) =>
  html
    .replace(/<[^>]+>/g, " ")
    .replace(/\s+/g, " ")
    .trim();

/** Renders the email for one recipient (or for the preview without options). */
export const renderEmail = (
  content: EmailContent,
  options: RenderOptions = {},
): RenderedEmail => {
  const links: EmailLink[] = [];
  const href = (url: string, label: string) => {
    const clean = url.trim();
    if (!isTrackable(clean)) return clean;
    const index = links.length;
    links.push({ url: clean, label: label.trim().slice(0, 120) });
    return options.trackLink ? options.trackLink(index, clean) : clean;
  };
  const name = (value: string) => fillName(value, options.firstName);

  /** Text with **bold**, *italic*, [label](url) and bare links. */
  const richText = (raw: string): { html: string; text: string } => {
    const filled = name(raw);
    let html = "";
    let text = "";
    const pattern =
      /\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)|(https?:\/\/[^\s<]+[^\s<.,;:!?)])/g;
    let last = 0;
    for (const match of filled.matchAll(pattern)) {
      const before = filled.slice(last, match.index);
      html += inline(before);
      text += before;
      const label = match[1] ?? match[3];
      const url = match[2] ?? match[3];
      html += `<a href="${escapeHtml(href(url, label))}" style="color:${INK};font-weight:700;text-decoration:underline">${escapeHtml(label)}</a>`;
      text += match[1] ? `${label} (${url})` : url;
      last = (match.index ?? 0) + match[0].length;
    }
    html += inline(filled.slice(last));
    text += filled.slice(last);
    return { html, text: text.replace(/\*\*|(?<!\*)\*(?!\*)/g, "") };
  };
  const inline = (value: string) =>
    escapeHtml(value)
      .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
      .replace(/(^|[^*])\*([^*\n]+)\*/g, "$1<em>$2</em>")
      .replace(/\n/g, "<br>");

  const button = (url: string, label: string, variant = "dark") => {
    const lime = variant === "lime";
    return `<table role="presentation" cellpadding="0" cellspacing="0" border="0" style="margin:8px 0 16px"><tr><td style="border-radius:999px;background:${lime ? LIME : INK}"><a href="${escapeHtml(href(url, label))}" style="display:inline-block;padding:14px 26px;border-radius:999px;color:${lime ? INK : "#ffffff"};font-weight:700;font-size:15px;text-decoration:none;font-family:Arial,Helvetica,sans-serif">${escapeHtml(label)}</a></td></tr></table>`;
  };

  /** Links inside pasted HTML are tracked too. */
  const trackHtml = (html: string) =>
    name(html).replace(
      /<a\b([^>]*?)href=(["'])(.*?)\2([^>]*)>([\s\S]*?)<\/a\s*>/gi,
      (all, before, quote, url, after, inner) =>
        isTrackable(url)
          ? `<a${before}href=${quote}${escapeHtml(href(url.replace(/&amp;/g, "&"), linkLabel(inner) || url))}${quote}${after}>${inner}</a>`
          : all,
    );

  const parts: string[] = [];
  const textParts: string[] = [];
  const plain = content.style === "plain";

  for (const block of content.blocks) {
    switch (block.type) {
      case "heading": {
        if (!block.text?.trim()) break;
        const value = name(block.text);
        parts.push(
          `<h2 style="margin:8px 0 12px;font-size:${plain ? 18 : 24}px;line-height:1.25;font-weight:800;letter-spacing:-0.3px;color:${INK}">${escapeHtml(value)}</h2>`,
        );
        textParts.push(value.toLocaleUpperCase("es"));
        break;
      }
      case "text": {
        if (!block.text?.trim()) break;
        const paragraphs = block.text.split(/\n\s*\n/);
        for (const paragraph of paragraphs) {
          const rich = richText(paragraph.trim());
          parts.push(
            `<p style="margin:0 0 14px;font-size:15px;line-height:1.6;color:${INK}">${rich.html}</p>`,
          );
          textParts.push(rich.text);
        }
        break;
      }
      case "image": {
        if (!block.url?.trim()) break;
        const img = `<img src="${escapeHtml(block.url)}" alt="${escapeHtml(block.alt ?? "")}" width="100%" style="display:block;width:100%;max-width:100%;height:auto;border:0;border-radius:${content.style === "html" ? 0 : 14}px">`;
        parts.push(
          `<div style="margin:0 0 16px">${
            block.link?.trim()
              ? `<a href="${escapeHtml(href(block.link, block.alt || "Imagen"))}" style="text-decoration:none">${img}</a>`
              : img
          }</div>`,
        );
        if (block.link?.trim())
          textParts.push(`${block.alt || "Ver"}: ${block.link}`);
        break;
      }
      case "button": {
        if (!block.url?.trim() || !block.label?.trim()) break;
        parts.push(button(block.url, name(block.label), block.variant));
        textParts.push(`${name(block.label)}: ${block.url}`);
        break;
      }
      case "video": {
        if (!block.url?.trim()) break;
        const label = block.label?.trim() || "Ver el vídeo";
        const thumb = block.thumbnail?.trim() || youtubeThumbnail(block.url);
        if (thumb) {
          const link = href(block.url, label);
          parts.push(
            `<div style="margin:0 0 6px"><a href="${escapeHtml(link)}" style="display:block;position:relative;text-decoration:none"><img src="${escapeHtml(thumb)}" alt="${escapeHtml(label)}" width="100%" style="display:block;width:100%;height:auto;border:0;border-radius:14px"></a></div>`,
          );
          // Same link again as a button (counted once as the same index)
          parts.push(
            `<table role="presentation" cellpadding="0" cellspacing="0" border="0" style="margin:8px 0 16px"><tr><td style="border-radius:999px;background:${INK}"><a href="${escapeHtml(link)}" style="display:inline-block;padding:12px 22px;border-radius:999px;color:#ffffff;font-weight:700;font-size:15px;text-decoration:none;font-family:Arial,Helvetica,sans-serif">&#9654;&nbsp; ${escapeHtml(label)}</a></td></tr></table>`,
          );
        } else {
          parts.push(button(block.url, `▶  ${label}`));
        }
        textParts.push(`${label}: ${block.url}`);
        break;
      }
      case "file": {
        if (!block.url?.trim()) break;
        const label =
          block.label?.trim() ||
          `Descargar ${block.name?.trim() || "el archivo"}`;
        parts.push(button(block.url, label, "lime"));
        textParts.push(`${label}: ${block.url}`);
        break;
      }
      case "html": {
        if (!block.html?.trim()) break;
        parts.push(
          `<div style="margin:0 0 16px">${trackHtml(block.html)}</div>`,
        );
        textParts.push(stripTags(name(block.html)));
        break;
      }
      case "divider": {
        parts.push(
          `<hr style="border:0;border-top:1px solid #e7e7e2;margin:20px 0">`,
        );
        textParts.push("—");
        break;
      }
    }
  }

  const unsubscribe = options.unsubscribeUrl ?? "#";
  const legal = `Recibes este correo porque te interesaste por Gula de Agua o eres cliente. Si no quieres recibir más correos, <a href="${escapeHtml(unsubscribe)}" style="color:${MUTED};text-decoration:underline">date de baja aquí</a>.`;
  const pixel = options.openPixelUrl
    ? `<img src="${escapeHtml(options.openPixelUrl)}" width="1" height="1" alt="" style="display:block;width:1px;height:1px;border:0">`
    : "";
  const preheader = content.preheader?.trim()
    ? `<div style="display:none;max-height:0;overflow:hidden;opacity:0;color:transparent">${escapeHtml(name(content.preheader))}${"&#8204;&nbsp;".repeat(40)}</div>`
    : "";
  const body = parts.join("\n");

  let inner: string;
  if (content.style === "plain") {
    inner = `<div style="max-width:600px;padding:16px;font-family:Arial,Helvetica,sans-serif;color:${INK}">
${body}
<p style="margin:28px 0 0;font-size:11px;line-height:1.5;color:${MUTED}">${legal}</p>
</div>`;
  } else if (content.style === "html") {
    inner = `<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="background:#f4f4f0"><tr><td align="center" style="padding:0">
<table role="presentation" width="600" cellpadding="0" cellspacing="0" border="0" style="width:100%;max-width:600px;background:#ffffff"><tr><td style="font-family:Arial,Helvetica,sans-serif;color:${INK}">
${body}
</td></tr></table>
<p style="max-width:560px;margin:16px auto 24px;padding:0 16px;font-family:Arial,Helvetica,sans-serif;font-size:11px;line-height:1.5;color:${MUTED};text-align:center">${legal}</p>
</td></tr></table>`;
  } else {
    const contact = `¿Dudas? Escríbenos por <a href="${escapeHtml(href(GULA_WHATSAPP, "WhatsApp"))}" style="color:${INK};font-weight:700">WhatsApp</a>, llámanos al ${GULA_PHONE} o responde a este correo.`;
    inner = `<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="background:#f4f4f0"><tr><td align="center" style="padding:24px 12px">
<table role="presentation" width="600" cellpadding="0" cellspacing="0" border="0" style="width:100%;max-width:600px">
<tr><td style="padding:0 8px 16px;font-family:Arial,Helvetica,sans-serif"><a href="${escapeHtml(href(GULA_WEB, "Logo"))}" style="text-decoration:none;color:${INK};font-size:30px;font-weight:800;letter-spacing:-1px">gula<span style="color:${LIME}">.</span></a></td></tr>
<tr><td style="background:#ffffff;border-radius:20px;padding:28px 28px 14px;font-family:Arial,Helvetica,sans-serif;color:${INK}">
${body}
</td></tr>
<tr><td style="padding:16px 8px 0;font-family:Arial,Helvetica,sans-serif">
<p style="margin:0 0 12px;padding:12px 16px;background:${LIME_SOFT};border-radius:12px;font-size:14px;line-height:1.5;color:${INK}">${contact}</p>
<p style="margin:0 0 8px;font-size:11px;line-height:1.5;color:${MUTED}">${legal}</p>
</td></tr>
</table>
</td></tr></table>`;
  }

  const subject = name(content.subject ?? "").trim();
  const html = `<!doctype html><html lang="es"><head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1"><meta name="color-scheme" content="light"><title>${escapeHtml(subject)}</title></head><body style="margin:0;padding:0;background:${content.style === "plain" ? "#ffffff" : "#f4f4f0"}">${preheader}${inner}${pixel}</body></html>`;

  const text = [
    ...textParts,
    "",
    content.style === "brand"
      ? `¿Dudas? WhatsApp ${GULA_WHATSAPP} · Teléfono ${GULA_PHONE}`
      : "",
    `Darse de baja: ${unsubscribe}`,
  ]
    .filter((line, index, all) => line || all[index - 1])
    .join("\n\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();

  return { subject, html, text, links };
};

/** The things the editor checks before sending. */
export const emailProblems = (content: EmailContent): string[] => {
  const problems: string[] = [];
  if (!content.subject?.trim()) problems.push("Falta el asunto");
  const filled = content.blocks.filter((block) => {
    switch (block.type) {
      case "heading":
      case "text":
        return !!block.text?.trim();
      case "html":
        return !!block.html?.trim();
      case "divider":
        return false;
      default:
        return !!block.url?.trim();
    }
  });
  if (!filled.length) problems.push("El correo está vacío");
  for (const block of content.blocks) {
    if (
      block.type === "button" &&
      block.url?.trim() &&
      !/^(https?:\/\/|mailto:|tel:)/i.test(block.url.trim())
    )
      problems.push(
        `El botón «${block.label}» no tiene un enlace válido (https://…)`,
      );
    if (block.type === "button" && block.url?.trim() && !block.label?.trim())
      problems.push("Hay un botón sin texto");
  }
  return problems;
};
